import type {
  DataGridColumnOrderState,
  DataGridColumnPinningState,
  DataGridFilterState,
  DataGridSortingState,
} from '@ibsys/kit';
import type { SearchDialogsPage } from '@/entities/search';
import type {
  DataGridColumnActions,
  DataGridColumnSettings,
  DataGridColumnStateProps,
  ExpertReportSettings,
} from '@/entities/settings';
import type { DialogMainColumn } from './types';
import { useDialogColumnsData } from './useDialogColumnsData';
import { useDialogColumnsState } from './useDialogColumnsState';
import { useDialogSearchParams } from './useDialogSearchParams';
import type { ContentSearchColumnOptions } from './useDialogTableColumns';
import { useEffectiveColumnOrder } from './useEffectiveColumnOrder';

/** Параметры подсистемы колонок списка диалогов */
type Params = {
  /** Настройки таблицы (персистенция колонок) */
  settings: DataGridColumnSettings;
  /** Экшены таблицы */
  actions: DataGridColumnActions;
  /** Фильтры грида */
  filters: DataGridFilterState;
  /** Сортировка грида */
  sorting: DataGridSortingState;
  /** Колонки поиска по содержимому (появляются при активном режиме) */
  contentSearch: ContentSearchColumnOptions;
  /** Отсекает id колонок поиска по содержимому из пользовательского порядка */
  filterUserColumnOrder: (columnOrder: string[]) => string[];
  /** Ставит пару колонок поиска по содержимому в начало порядка */
  getEffectiveColumnOrder: (userColumnOrder: string[]) => string[];
  /** Сохранённый в профиле состав основных колонок */
  mainColumns: string[] | undefined;
  /** Записывает состав основных колонок в настройки профиля */
  onMainColumnsChange: (columns: DialogMainColumn[]) => void;
};

/** Колонки таблицы диалогов и параметры запроса из их видимости */
type Result = {
  /** Определения колонок DataGrid */
  columns: ReturnType<typeof useDialogColumnsData>['columns'];
  /** Данные колонок ещё грузятся (атрибуты/отчёты) */
  isLoading: boolean;
  /** Стейт колонок готов */
  isReady: boolean;
  /** Пропы колонок грида с эффективным порядком */
  gridColumnProps: DataGridColumnStateProps;
  /** Эффективный порядок колонок */
  columnOrder: DataGridColumnOrderState;
  /** Закрепление колонок */
  columnPinning: DataGridColumnPinningState;
  /** Пути видимых атрибутов */
  visibleAttributePaths: string[];
  /** Видимые основные колонки */
  visibleMainColumns: DialogMainColumn[];
  /** Параметры видимых экспертных заключений */
  expertReportSearchParams: ExpertReportSettings[];
  /** Собранный `dialogsPage` для запроса списка */
  searchDialogsPage: SearchDialogsPage;
};

/**
 * Подсистема колонок списка диалогов: строит определения колонок из данных бэкенда, ведёт стейт их
 * видимости/порядка/закрепления с персистенцией, накладывает эффективный порядок в режиме поиска по
 * содержимому и собирает из текущей видимости параметры запроса списка (атрибуты, основные колонки,
 * экспертные заключения).
 */
export const useDialogColumns = ({
  settings,
  actions,
  filters,
  sorting,
  contentSearch,
  filterUserColumnOrder,
  getEffectiveColumnOrder,
  mainColumns,
  onMainColumnsChange,
}: Params): Result => {
  const { columns, defaultVisibility, allAttributes, allReports, blocksByReportId, isLoading } = useDialogColumnsData({
    contentSearch,
  });

  const { gridColumnProps, columnVisibility, isReady } = useDialogColumnsState(
    settings,
    actions,
    filters,
    sorting,
    defaultVisibility,
    isLoading,
  );

  const { effectiveColumnOrder, effectiveGridColumnProps } = useEffectiveColumnOrder({
    gridColumnProps,
    filterUserColumnOrder,
    getEffectiveColumnOrder,
  });

  const { visibleAttributePaths, visibleMainColumns, expertReportSearchParams, searchDialogsPage } =
    useDialogSearchParams({
      allAttributes,
      allReports,
      blocksByReportId,
      columnVisibility,
      isReady,
      mainColumns,
      onMainColumnsChange,
    });

  return {
    columns,
    isLoading,
    isReady,
    gridColumnProps: effectiveGridColumnProps,
    columnOrder: effectiveColumnOrder,
    columnPinning: gridColumnProps.columnPinning,
    visibleAttributePaths,
    visibleMainColumns,
    expertReportSearchParams,
    searchDialogsPage,
  };
};
